// Innlogging med persontoken.
//
// Det finnes ikke brukernavn og passord i dugnad-tjenesten — den autentiserer
// med `Authorization: Bearer <persontoken>`, og tokenet fikk man én gang ved
// registrering. Skjermen gjør derfor to ting: tar imot tokenet, og PRØVER det
// mot `GET /meg` før den slipper noen inn. Å lagre et token vi ikke har
// verifisert ville gitt en app som ser innlogget ut og feiler på hvert kall.
import { lagKlient, type Klient } from '../api/klient.js';
import { avkrysning, div, felt, h, knapp, p, sett, span } from '../ui/dom.js';
import { ikon } from '../ui/ikoner.js';
import { feilboks } from '../ui/tilstand.js';
import { serUtSomToken } from '../lib/token-lager.js';
import type { MegSvar } from '../api/typer.js';

export interface InnloggingValg {
  basis: string;
  hent?: typeof fetch;
  /** Kalles med et VERIFISERT token. */
  påInnlogget(token: string, husk: boolean, meg: MegSvar): void;
  /** Beskjed fra skallet, f.eks. «Tokenet er ikke gyldig lenger». */
  beskjed?: string;
}

export function visInnlogging(rot: HTMLElement, valg: InnloggingValg): void {
  rot.className = 'reg-rot';
  const kort = div('reg-kort');
  sett(rot, kort);

  const merke = div('reg-merke');
  merke.append(span('reg-logo', 'PROSJEKT'), span('reg-under', 'Frivillighetstimer — Heimevernet'));
  kort.append(merke);

  const innhold = div('reg-innhold');
  kort.append(innhold);
  innhold.append(h(1, 'reg-tittel', 'Logg inn'));
  innhold.append(p('reg-brød', 'Lim inn persontokenet du fikk da du registrerte deg.'));

  if (valg.beskjed) {
    const b = div('reg-beskjed');
    b.setAttribute('role', 'status');
    b.append(ikon('info'), span('', valg.beskjed));
    innhold.append(b);
  }

  const skjema = document.createElement('form');
  skjema.className = 'reg-skjema';
  skjema.noValidate = true;

  const token = felt('Persontoken', {
    // `password` skjuler tokenet mot skulderkikking og skjermdeling. Vis-knappen
    // under gjør at man likevel kan kontrollere at limingen ble riktig.
    type: 'password',
    plassholder: 'dgn_…',
    autofullfør: 'off',
    påkrevd: true,
  });
  token.input.spellcheck = false;

  const vis = knapp('Vis token', () => {
    const skjult = token.input.type === 'password';
    token.input.type = skjult ? 'text' : 'password';
    vis.textContent = skjult ? 'Skjul token' : 'Vis token';
    vis.setAttribute('aria-pressed', String(skjult));
  }, { variant: 'lenke' });
  vis.setAttribute('aria-pressed', 'false');
  vis.classList.add('vis-token');

  const husk = avkrysning('Husk meg på denne maskinen', true);
  const huskHjelp = p('felt-hjelp',
    'På: tokenet lagres i nettleseren og overlever at du lukker den. Av: det ligger '
    + 'bare i denne fanen og glemmes når du lukker den. Uansett kan et skript som '
    + 'kjører på siden lese tokenet — bruk en maskin du stoler på, og «Logg ut» når '
    + 'du er ferdig på en delt maskin.');

  const feilplass = div('skjema-feilplass');
  const send = knapp('Logg inn', undefined, { variant: 'primær', type: 'submit' });

  skjema.append(token.rot, vis, husk.rot, huskHjelp, feilplass, send);
  innhold.append(skjema);

  const hjelp = div('reg-fot');
  hjelp.append(ikon('lenke'));
  hjelp.append(span('', 'Har du fått en registreringslenke? Åpne den for å lage konto — '
    + 'du trenger ikke token først.'));
  innhold.append(hjelp);

  token.input.focus();

  skjema.addEventListener('submit', (e) => {
    e.preventDefault();
    void loggInn();
  });

  async function loggInn(): Promise<void> {
    token.settFeil(null);
    sett(feilplass);
    const t = token.input.value.trim();
    if (!t) { token.settFeil('Lim inn tokenet.'); return token.input.focus(); }
    if (!serUtSomToken(t)) {
      token.settFeil('Dette ser ikke ut som et persontoken. Formen er «dgn_…», med et punktum i.');
      return token.input.focus();
    }

    send.disabled = true;
    const før = send.textContent;
    send.textContent = 'Kontrollerer …';
    // Egen klient med akkurat dette tokenet: vi verifiserer FØR vi lagrer noe.
    const prøve: Klient = lagKlient({ basis: valg.basis, hent: valg.hent, token: () => t });
    const r = await prøve.meg();
    send.disabled = false;
    send.textContent = før;

    if (r.ok && r.data?.person?.id) {
      valg.påInnlogget(t, husk.input.checked, r.data);
      return;
    }
    if (r.ok) {
      sett(feilplass, feilboks({
        art: 'ukjent', status: 200, kanPrøvePåNytt: true,
        melding: 'Tjenesten svarte uten person. Tokenet kan være gyldig, men svaret hadde ikke '
          + 'formen klienten venter. Meld fra om avviket.',
      }, () => void loggInn()));
      return;
    }
    if (r.feil.art === 'uautentisert') {
      token.settFeil('Tjenesten godtok ikke tokenet. Er det trukket tilbake, eller limt inn halvt?');
      token.input.focus();
      return;
    }
    sett(feilplass, feilboks(r.feil, () => void loggInn()));
  }
}
